import 'extensions/ui-components/x-quantity';
import {imageFallback} from 'extensions/ui-directives';
import {defineCustomElement, formatPrice} from 'extensions/ui-utils';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';

export class SummaryProductLine extends LitElement {
	static get properties () {
		return {
			category: {
				type: String
			},
			coolingCapacity: {
				attribute: 'cooling-capacity',
				type: String
			},
			empty: {
				type: Boolean
			},
			gasLevy: {
				attribute: 'gas-levy',
				type: Number
			},
			heatingCapacity: {
				attribute: 'heating-capacity',
				type: String
			},
			imageUrl: {
				attribute: 'image-url',
				type: String
			},
			label: {
				type: String
			},
			price: {
				type: String
			},
			quantity: {
				type: Number
			},
			refrigerantType: {
				attribute: 'refrigerant-type',
				type: String
			},
			showQuantity: {
				attribute: 'show-quantity',
				type: Boolean
			},
			sku: {
				type: String
			}
		};
	}

	get formattedGasLevy () {
		return formatPrice(this.gasLevy);
	}

	// eslint-disable-next-line max-lines-per-function
	constructor () {
		super();

		/**
		 * @type {string}
		 */
		this.category = '';

		/**
		 * @type {String}
		 */
		this.coolingCapacity = '';

		/**
		 * @type {boolean}
		 */
		this.empty = false;

		/**
		 * @type {String}
		 */
		this.heatingCapacity = '';

		/**
		 * @type {number}
		 */
		this.gasLevy = 0;

		/**
		 * @type {string}
		 */
		this.imageUrl = '';

		/**
		 * @type {string}
		 */
		this.label = '';

		/**
		 * @type {string}
		 */
		this.price = '';

		/**
		 * @type {number}
		 */
		this.quantity = 1;

		/**
		 * @type {'R32'|'R410'}
		 */
		this.refrigerantType = '';

		/**
		 * @type {boolean}
		 */
		this.showQuantity = false;

		/**
		 * @type {string}
		 */
		this.sku = '';
	}

	createRenderRoot () {
		return this;
	}

	handleProductDelete () {
		this.dispatchEvent(new CustomEvent('delete', {
			bubbles: true
		}));
	}

	renderProductColumn () {
		if (this.empty) {
			return 'Not Yet Started';
		}

		return html`
			<div class="product-group">
				<div class="product-image"><img src="${this.imageUrl}" alt="${this.sku} image" ${imageFallback(window.mivaJS.Image_Not_Found_Grid)} /></div>
				<div class="product-info">
					<div class="product-category">${this.category}</div>
					<div class="product-sku">${this.sku}</div>

					<div class="product-capacity-group capacity-group">
						${this.coolingCapacity ? html`<x-capacity-flag type="cooling">${this.coolingCapacity}</x-capacity-flag>` : nothing}
						${this.heatingCapacity ? html`<x-capacity-flag type="heating">${this.heatingCapacity}</x-capacity-flag>` : nothing}
					</div>
				</div>
			</div>
		`;
	}

	renderQuantityColumn () {
		if (this.empty) {
			return html`&ndash;`;
		}

		if (this.showQuantity) {
			return html`
				<div class="quantity-container">
					<x-quantity value="${this.quantity}"></x-quantity>
					<button @click="${this.handleProductDelete}" class="empty-button delete-button" aria-label="Remove Product"></button>
				</div>
			`;
		}

		return this.quantity;
	}

	renderPriceColumn () {
		if (this.empty) {
			return 'TBD';
		}

		if (!window.mivaJS?.Customer_Permissions?.WebstorePricing) {
			return html`<span class="price price-hyphen"></span>`;
		}

		return html`
			${this.price}
			${this.renderGasLevy()}
		`;
	}

	renderGasLevy () {
		if (!this.gasLevy) {
			return nothing;
		}

		return html`
			<div class="gas-levy-container">
				<span class="gas-levy-label">${this.refrigerantType} Gas Levy</span>
				<span class="price">${this.formattedGasLevy}</span>
			</div>
		`;
	}

	render () {
		const classes = {
			empty: this.empty,
			'grid-table__row': true,
			'grid-table__row-show-quantity': this.showQuantity
		};

		return html`
			<div class="${classMap(classes)}">
				<div class="grid-table__column grid-table__column-label">${this.label}</div>
				<div class="grid-table__column grid-table__column-product">
					${this.renderProductColumn()}
				</div>
				<div class="grid-table__column grid-table__column-capacity">
					<div class="capacity-group">
						${this.coolingCapacity ? html`<x-capacity-flag type="cooling">${this.coolingCapacity}</x-capacity-flag>` : nothing}
						${this.heatingCapacity ? html`<x-capacity-flag type="heating">${this.heatingCapacity}</x-capacity-flag>` : nothing}
					</div>
				</div>
				<div class="grid-table__column grid-table__column-quantity">${this.renderQuantityColumn()}</div>
				<div class="grid-table__column right grid-table__column-pricing">${this.renderPriceColumn()}</div>
			</div>
		`;
	}
};

defineCustomElement('x-summary-product-line', SummaryProductLine);
